import Nutshell from '../../Nutshell';
import Helpers from '../../core/Helpers';

import RSA_OAEP  from './lib/RSA_OAEP';
import RSA_OAEP_AES256_CBC from './lib/RSA_OAEP_AES256_CBC';
import Utils from './lib/Utils';

/**
 * Helper class to encrypt data with RSA AES
 *
 * @author Michel Heitbrink
 */

import ServerRequest from '../../ServerRequest';
import Plugin from '../../core/Plugin'

class Encryption extends Plugin {

	static pluginName = "Encryption";

	static getDefaults() {
		return {
			bitsize: 2048
		}
	}

	static getName() {
        return Encryption.pluginName;
    }

	constructor( options ) {

		super(options);

		this.keys = { client: {}, server: {} };

		if ( !SUPPORT.encryption ) {
			console.warn('@nutshell/core: Encryption not supported. Set config.support.encryption on `true` in app.config.js');
		}
	}

	/**
	 * Encrypt payload
	 * @param  {Object} payload
	 * @return {Promise}
	 */
	encrypt( payload, encode ) {

		encode = ( typeof encode !== "boolean" ) ? true : encode; //By default encode

		return new Promise( ( resolve, reject ) => {

            this.getClientKeys().then( ( clientKeys ) => {

                this.getServerKeys().then( ( serverKeys ) => {

					RSA_OAEP_AES256_CBC.encrypt(payload, serverKeys.public).then( (encrypted) => {
						resolve( ( encode ) ? Utils.btoa(encrypted) : encrypted );

					}).catch(reject);

                }).catch( reject );

            }).catch( reject );

		});
	}

    /**
	 * Decrypt data
	 * @param  {String} encrypted
	 * @param  {Boolean} decode
	 * @return {Promise}
	 */
	decrypt( encrypted, decode ) {

		decode = ( typeof decode !== "boolean" ) ? true : decode; //By default decode

		return new Promise( ( resolve, reject ) => {

            this.getClientKeys().then( ( clientKeys ) => {

				encrypted = ( decode ) ? Utils.btoa(encrypted) : encrypted;

				RSA_OAEP_AES256_CBC.decrypt( encrypted, this.keys.client.private ).then( ( decrypted ) => {

					if ( decrypted instanceof Uint8Array ) {
                        decrypted = new TextDecoder().decode(decrypted);
                    }

					resolve(decrypted);

				}).catch(reject);

            }).catch( reject );
		});
	}

	/**
	 * Converts server keys to OpenPGP PublicKey.
	 * @return {Promise}
	 */
	getClientKeys() {

        return new Promise( (resolve, reject) => {

            if ( this.keys.client && Object.keys(this.keys.client).length === 2 ) {
                resolve(this.keys.client);
            }
			else {

                if ( window.sessionStorage.rsa_client_public && window.sessionStorage.rsa_client_private ) {

					RSA_OAEP.importPublicKeyPEM(window.sessionStorage.rsa_client_public).then( (publicKey) => {

						if ( publicKey ) {

							this.keys.client.public = publicKey;

							RSA_OAEP.importPrivateKeyPEM(window.sessionStorage.rsa_client_private).then( (privateKey) => {

								if ( privateKey ) {

									this.keys.client.private = privateKey;

									resolve(privateKey);
								}
								else {
									rejec();
								}

							}).catch(reject);

						}
						else {
							reject();
						}

					}).catch(reject);

                }
				else {

					let keys = RSA_OAEP.genkeys( this.options.bitsize );

					RSA_OAEP.genkeys( this.options.bitsize ).then( (keys) => {

						if ( keys ) {

							this.keys.client = keys;

							RSA_OAEP.exportPublicKeyPEM(keys.public).then( (pem_public) => {

								if ( pem_public ) {

									window.sessionStorage.rsa_client_public = pem_public;

									RSA_OAEP.exportPrivateKeyPEM(keys.private).then( (pem_private) => {

										if ( pem_private ) {
											window.sessionStorage.rsa_client_private = pem_private;
											resolve(this.keys.client);
										}
										else {
											reject();
										}

									}).catch(reject);
								}
								else {
									reject();
								}

							}).catch(reject);
						}
						else {
							reject();
						}

					});
                }
            }
        });
	}

    getServerKeys() {

        const request = new ServerRequest();

        return new Promise( (resolve, reject) => {

            if ( this.keys.client && Object.keys(this.keys.client).length ) {

                if ( this.keys.server && Object.keys(this.keys.server).length ) {

                    resolve( this.keys.server );

                } else {

                    if ( window.sessionStorage.rsa_server_public ) {

						RSA_OAEP.importPublicKeyPEM( window.sessionStorage.rsa_server_public ).then( (publickey) => {

							if ( publickey ) {
								this.keys.server.public = publickey;
								resolve( this.keys.server );
							}
							else {
								reject();
							}

						}).catch(reject);

                    } else {

                        request.post(null, window.sessionStorage.rsa_client_public, { encrypt: false, encode: 'base64' }, null, { 'X-Method': 'secure' } ).then( ( { result } ) => {

                            if ( result ) {

								/**
								 * Decrypt base64 result
								 */
								if ( Helpers.isBase64(result) ) {
									result = atob(result);
								}

								RSA_OAEP.importPublicKeyPEM( result ).then( (publickey) => {

									if ( publickey ) {

										window.sessionStorage.rsa_server_public = result;
										this.keys.server.public = publickey;

										resolve( this.keys.server );
									}
									else {
										reject();
									}

								}).catch(reject);
                            }

                        }).catch(reject);
                    }
                }

            } else {
                reject('No client keys set. Required to get server key.');
            }
        });
    }

	resetKeys() {

		this.keys = { client: {}, server: {} };

		window.sessionStorage.removeItem('rsa_server_public');
		window.sessionStorage.removeItem('rsa_client_public');
		window.sessionStorage.removeItem('rsa_client_private');
	}
}

export default Encryption;

/*
---
name: RSA AES Encryption
category: JavaScript
---
Helper class to encrypt / decrypt data with rsa/aes.

Constructor
======

##### Arguments
- **options** (Object) - Object with options.

##### Options
- **bitsize** (Integer) - Size of the encrypted data. Possible options: 1024, 2048 or 4096. Default: 2048

```js
const encryption = new Encryption( options );
```

Decrypt
======

##### Arguments
- **encrypted** (String) -  Apply decrypt on this data
- **decode** (Boolean) - Decode base64 before decrypting. Default true

```js
const encryption = new Encryption();
encryption.decrypt(data, true).then( (decrypted) => {});
```

Encrypt
======

##### Arguments
- **data** (String|Object) - Data to decrypt
- **decode** (Boolean) - Decode base64 before decrypting. Default true

```js
const encryption = new Encryption();
encryption.encrypt(data, true).then( (encrypted) => {});
```
*/
